/**
 * PageErrorBoundary - Reusable per-route error boundary.
 *
 * Wraps lazy-loaded page components to catch render errors and display
 * a friendly fallback UI instead of crashing the entire application.
 */

import { ErrorBoundary, createSignal, Show } from "solid-js";
import type { JSX } from "solid-js";
import { trace } from "../lib/trace";

const CHUNK_ERROR_RELOAD_GUARD_KEY = "airbrx.cp.chunk-reload-at";
const CHUNK_ERROR_RELOAD_GUARD_MS = 5 * 60 * 1000;

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const message = err.message.toLowerCase();
  return message.includes("failed to fetch dynamically imported module")
    || message.includes("error loading dynamically imported module")
    || message.includes("importing a module script failed")
    || message.includes("chunkloaderror");
}

function shouldAutoReloadChunkError(): boolean {
  try {
    const raw = sessionStorage.getItem(CHUNK_ERROR_RELOAD_GUARD_KEY);
    if (!raw) return true;
    const previousAttempt = Number(raw);
    if (!Number.isFinite(previousAttempt)) return true;
    return (Date.now() - previousAttempt) > CHUNK_ERROR_RELOAD_GUARD_MS;
  } catch {
    // If session storage is unavailable, still try a single reload recovery.
    return true;
  }
}

function markChunkAutoReloadAttempted(): void {
  try {
    sessionStorage.setItem(CHUNK_ERROR_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures; reload can still proceed.
  }
}

function userFacingMessage(err: unknown): string {
  if (!(err instanceof Error)) return "This page could not be displayed. Refresh and try again.";
  if (isChunkLoadError(err)) {
    return "This page update did not finish loading. Refresh to sync the latest assets and try again.";
  }
  const message = err.message.toLowerCase();
  if (message.includes("network") || message.includes("failed to fetch")) {
    return "This page could not finish loading because the network request failed. Check the connection and try again.";
  }
  return "This page hit an unexpected problem. Refresh and try again, or return home if the issue persists.";
}

/**
 * Error fallback with warning icon, retry button, collapsible stack trace (dev only),
 * and a secondary "Go Home" link.
 */
function ErrorFallback(props: { err: unknown; reset: () => void }): JSX.Element {
  const technicalMessage =
    props.err instanceof Error ? props.err.message : "An unexpected error occurred";
  const stack = props.err instanceof Error ? props.err.stack : undefined;
  const message = userFacingMessage(props.err);

  const [showDetails, setShowDetails] = createSignal(false);

  trace.render("ErrorBoundary caught error", {
    message: technicalMessage,
    stack: stack?.slice(0, 500),
    path: typeof window !== "undefined" ? window.location.pathname : "unknown",
  }, "error");

  // Recover automatically from stale lazy-route chunk imports once per guard window.
  if (typeof window !== "undefined" && isChunkLoadError(props.err) && shouldAutoReloadChunkError()) {
    markChunkAutoReloadAttempted();
    trace.lifecycle("Auto-reloading after chunk load failure", {
      message,
      path: window.location.pathname,
    }, "error");
    queueMicrotask(() => {
      window.location.reload();
    });
  }

  return (
    <div
      role="alert"
      style={{
        "min-height": "60vh",
        display: "flex",
        "align-items": "center",
        "justify-content": "center",
        padding: "2rem",
      }}
    >
      <div style={{ "text-align": "center", "max-width": "480px" }}>
        {/* Warning triangle icon */}
        <div style={{ "margin-bottom": "var(--space-4, 1rem)" }}>
          <svg aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "48px", height: "48px", margin: "0 auto", color: "var(--error, #ef4444)" }}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <h2
          style={{
            "font-size": "1.5rem",
            "font-weight": "600",
            "margin-bottom": "0.75rem",
            color: "var(--error, #ef4444)",
          }}
        >
          Something went wrong
        </h2>
        <p
          style={{
            color: "var(--text-secondary, #6b7280)",
            "margin-bottom": "1.5rem",
            "line-height": "1.5",
          }}
        >
          {message}
        </p>
        <Show when={technicalMessage && technicalMessage !== message}>
          <p
            style={{
              color: "var(--text-tertiary, #9ca3af)",
              "margin-bottom": "1.5rem",
              "line-height": "1.4",
              "font-size": "0.875rem",
            }}
          >
            {technicalMessage}
          </p>
        </Show>

        {/* Actions */}
        <div style={{ display: "flex", "justify-content": "center", gap: "var(--space-3, 0.75rem)", "flex-wrap": "wrap", "margin-bottom": "var(--space-4, 1rem)" }}>
          <button
            onClick={() => {
              props.reset();
              window.location.reload();
            }}
            style={{
              display: "inline-flex",
              "align-items": "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "var(--brand-primary, #2563eb)",
              color: "white",
              "border-radius": "0.375rem",
              "font-weight": "500",
              border: "none",
              cursor: "pointer",
              "font-size": "0.875rem",
            }}
          >
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" style={{ width: "16px", height: "16px" }} viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
            </svg>
            Try Again
          </button>
          <a
            href="/"
            style={{
              display: "inline-flex",
              "align-items": "center",
              padding: "0.75rem 1.5rem",
              background: "transparent",
              color: "var(--text-secondary, #6b7280)",
              "border-radius": "0.375rem",
              "text-decoration": "none",
              "font-weight": "500",
              border: "1px solid var(--border, #e5e7eb)",
              "font-size": "0.875rem",
            }}
          >
            Go Home
          </a>
        </div>

        {/* Collapsible stack trace - dev only */}
        <Show when={import.meta.env.DEV && stack}>
          <div style={{ "margin-top": "var(--space-4, 1rem)" }}>
            <button
              onClick={() => setShowDetails(!showDetails())}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-secondary, #6b7280)",
                cursor: "pointer",
                "font-size": "0.75rem",
                "text-decoration": "underline",
              }}
            >
              {showDetails() ? "Hide Error Details" : "Show Error Details"}
            </button>
            <Show when={showDetails()}>
              <pre
                style={{
                  "margin-top": "var(--space-3, 0.75rem)",
                  padding: "var(--space-3, 0.75rem)",
                  background: "var(--bg-secondary, #f3f4f6)",
                  "border-radius": "0.375rem",
                  "font-size": "0.75rem",
                  "font-family": "ui-monospace, monospace",
                  overflow: "auto",
                  "max-height": "200px",
                  "white-space": "pre-wrap",
                  "text-align": "left",
                  color: "var(--text-secondary, #6b7280)",
                }}
              >
                {stack}
              </pre>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
}

export const PageErrorBoundary = (props: { children: JSX.Element }) => (
  <ErrorBoundary fallback={(err, reset) => <ErrorFallback err={err} reset={reset} />}>
    {props.children}
  </ErrorBoundary>
);
